import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import api from "../../utils/api";
import UserActions from "./UserActions";
import UserFormDialog from "./UserFormDialog";
import UserDeleteDialog from "./UserDeleteDialog";
import BulkInviteButton from "./BulkInviteButton";
import UserResetPasswordDialog from "./UserResetPasswordDialog";
import UserResetSuccessDialog from "./UserResetSuccessDialog";

const AllUsersTable = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  useEffect(() => {
    setFilteredUsers(
      users.filter((user) =>
        ["Username", "Name", "Lastname", "Email"]
          .map((key) => user[key]?.toLowerCase())
          .some((value) => value && value.includes(searchTerm.toLowerCase()))
      )
    );
  }, [users, searchTerm]);

  const loadUsers = async () => {
    try {
      const response = await api.get("/users-by-role");

      const userMap = new Map();

      response.data.forEach((row) => {
        if (!userMap.has(row.Username)) {
          userMap.set(row.Username, {
            Username: row.Username,
            UserID: row.UserID,
            Name: row.Name,
            Lastname: row.Lastname,
            Email: row.Email,
            Status: row.status || row.Status,

            // ✅ Associated Accounts (same logic as PropertyAccountsTable)
            AssociatedAccounts: Array.isArray(row.AssociatedAccounts)
              ? row.AssociatedAccounts
              : row.AssociatedAccounts
              ? String(row.AssociatedAccounts)
                  .split(",")
                  .map((a) => a.trim())
              : [],

            Roles: [],
          });
        }

        userMap.get(row.Username).Roles.push({
          RoleID: row.RoleID,
          RoleName: row.RoleName,
        });
      });

      const grouped = Array.from(userMap.values());
      const withValidRoles = grouped.filter((user) =>
        user.Roles.some((r) => r.RoleID !== 3)
      );
      const activeUsers = withValidRoles.filter(
        (user) => user.Status?.toLowerCase() !== "inactive"
      );

      setUsers(activeUsers);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get("/roles");
      setAvailableRoles(response.data);
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      ...user,
      Roles: user.Roles?.map((r) => r.RoleID) || [],
      // keep AssociatedAccounts in formData if your dialog supports it
      AssociatedAccounts: user.AssociatedAccounts || [],
    });
    setFormMode("edit");
    setShowForm(true);
  };

  const handleSubmitUser = async () => {
    try {
      if (formMode === "edit") {
        await api.put(`/users/${formData.UserID}`, formData);
      } else {
        if (
          !formData.Password ||
          !formData.Username ||
          !formData.Roles?.length
        ) {
          alert("Username, password, and at least one role are required.");
          return;
        }

        await api.post("/addUser", {
          username: formData.Username,
          password: formData.Password,
          email: formData.Email || "default@example.com",
          name: formData.Name || "",
          lastname: formData.Lastname || "",
          roles: formData.Roles,
          sendInvitationEmail: formData.SendInvite || false,

          // Optional: only include if your backend supports it for All Users flow
          associatedAccounts: formData.AssociatedAccounts || [],
        });
      }

      setShowForm(false);
      loadUsers();
    } catch (error) {
      console.error("Error submitting user:", error);
      alert("Failed to submit user.");
    }
  };

  const handleResetPassword = (username) => {
    setUserToReset(username);
    setShowResetDialog(true);
  };

  const confirmResetPassword = async () => {
    try {
      const res = await api.post(`/users/${userToReset}/resetPassword`);
      setResetResult({
        username: userToReset,
        password: res.data.message?.split(": ")[1],
      });
    } catch (err) {
      console.error("Failed to reset password:", err);
      alert("Failed to reset password.");
    } finally {
      setShowResetDialog(false);
    }
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setShowDeleteDialog(true);
  };

  const handleSelectUser = (username) => {
    setSelectedUsers((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allUsernames = filteredUsers.map((user) => user.Username);
      setSelectedUsers(allUsernames);
    } else {
      setSelectedUsers([]);
    }
  };

  const isAllSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUsers.includes(user.Username));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%" }}>
      {/* Header Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          label="Search Users"
          size="small"
          fullWidth
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 400 }}
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setFormData({
                Username: "",
                Password: "",
                Name: "",
                Lastname: "",
                Email: "",
                Roles: [],
                SendInvite: true,
                AssociatedAccounts: [],
              });
              setFormMode("add");
              setShowForm(true);
            }}
          >
            New User
          </Button>

          <BulkInviteButton
            selectedUsers={selectedUsers}
            onSuccess={() => console.log("Invites sent!")}
          />
        </Box>
      </Box>

      {/* Desktop Table */}
      <TableContainer
        component={Paper}
        sx={{
          display: { xs: "none", lg: "block" },
          maxHeight: "calc(100vh - 250px)",
          width: "100%",
          overflowX: "auto",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: "max-content", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  textAlign: "center",
                  width: 50,
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  sx={{
                    color: "#fff",
                    "&.Mui-checked": { color: "#fff" },
                    p: 0.5,
                  }}
                />
              </TableCell>

              {[
                "Username",
                "Name",
                "Lastname",
                "Email",
                "Associated Accounts",
                "Roles",
                "Status",
                "Actions",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    whiteSpace: "nowrap",
                    maxWidth: 200,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.Username} hover>
                <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                  <Checkbox
                    checked={selectedUsers.includes(user.Username)}
                    onChange={() => handleSelectUser(user.Username)}
                    color="primary"
                    sx={{ p: 0.5 }}
                  />
                </TableCell>

                <TableCell>{user.Username}</TableCell>
                <TableCell>{user.Name}</TableCell>
                <TableCell>{user.Lastname}</TableCell>
                <TableCell>{user.Email}</TableCell>

                {/* ✅ Associated Accounts */}
                <TableCell>
                  {user.AssociatedAccounts?.join(", ") || "None"}
                </TableCell>

                <TableCell>
                  {user.Roles?.map((r) => r.RoleName).join(", ") || "No Roles"}
                </TableCell>

                <TableCell>
                  <Typography
                    color={user.Status?.toLowerCase() === "active" ? "green" : "red"}
                  >
                    {user.Status}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <UserActions
                      user={user}
                      onEdit={handleEditUser}
                      onResetPassword={handleResetPassword}
                      onDelete={handleDeleteUser}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {filteredUsers.map((user) => (
          <Paper
            key={user.Username}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Checkbox
                checked={selectedUsers.includes(user.Username)}
                onChange={() => handleSelectUser(user.Username)}
                color="primary"
                sx={{ mr: 1 }}
              />
              <Typography variant="subtitle1" fontWeight="bold">
                {user.Username}
              </Typography>
            </Box>

            <Typography>
              Name: {user.Name} {user.Lastname}
            </Typography>
            <Typography>Email: {user.Email}</Typography>

            {/* ✅ Associated Accounts */}
            <Typography>
              Associated Accounts: {user.AssociatedAccounts?.join(", ") || "None"}
            </Typography>

            <Typography>
              Status:{" "}
              <span
                style={{
                  color: user.Status?.toLowerCase() === "active" ? "green" : "red",
                }}
              >
                {user.Status}
              </span>
            </Typography>

            <Typography>
              Roles: {user.Roles?.map((r) => r.RoleName).join(", ") || "No Roles"}
            </Typography>

            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <UserActions
                user={user}
                onEdit={handleEditUser}
                onResetPassword={handleResetPassword}
                onDelete={handleDeleteUser}
              />
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Dialogs */}
      <UserFormDialog
        open={showForm}
        mode={formMode}
        formData={formData}
        setFormData={setFormData}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitUser}
        reloadUsers={loadUsers}
        availableRoles={availableRoles}
        // If your dialog supports it, you can enable associated accounts UI:
        // showAssociatedAccount={true}
      />

      <UserResetPasswordDialog
        open={showResetDialog}
        username={userToReset}
        onClose={() => setShowResetDialog(false)}
        onConfirm={confirmResetPassword}
      />

      <UserResetSuccessDialog
        open={!!resetResult}
        username={resetResult?.username}
        password={resetResult?.password}
        onClose={() => setResetResult(null)}
      />

      <UserDeleteDialog
        open={showDeleteDialog}
        username={userToDelete}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          try {
            await api.put(`/users/${userToDelete}/deactivate`);
            setShowDeleteDialog(false);
            loadUsers();
          } catch (err) {
            console.error("Failed to deactivate user:", err);
            alert("Failed to deactivate user.");
          }
        }}
      />
    </Box>
  );
};

export default AllUsersTable;
